import { useForm } from 'react-hook-form';
import useSaveSimulationPricingsDailyBenefitMutation from './useSaveSimulationPricingsDailyBenefitMutation';
import {
  always,
  and,
  applySpec,
  assocPath,
  compose,
  dissoc,
  dissocPath,
  find,
  fromPairs,
  isNil,
  lensIndex,
  map,
  not,
  over,
  path,
  pathOr,
  prop,
  propEq,
  propOr,
  toLower,
  toPairs,
  when
} from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { getLabelFromValue } from '@@viewCommonComponents/forms/options';
import { useEffect, useState } from 'react';

const findPriceProperty = (property, levelChosen) =>
  compose(propOr(0, property), find(propEq('level', levelChosen)));

const useSimulationPricingsDailyBenefitForm = ({
  getMiddleBoolean,
  getMiddleText,
  getSimulationPricingsDailyBenefit
}) => {
  const [loading, setLoading] = useState(false);
  const { onFormSubmit: saveSimulationPricingsDailyBenefit } =
    useSaveSimulationPricingsDailyBenefitMutation();
  const { processHandlers, context } = useProcess();

  const {
    isDutyOfAdviceSkippedDefaultValue,
    pricingLevelDefaultValue,
    pricings,
    contractOnGoingDefaultValue,
    commentDefaultValue
  } = applySpec({
    pricingLevelDefaultValue: prop('selectedLevel'),
    pricings: propOr([], 'pricings'),
    isDutyOfAdviceSkippedDefaultValue: pathOr(
      getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GARANTIES_GUARANTEE_DEFAULT_VALUE'),
      ['collectionNeeds', 0, 'isDutyOfAdviceSkipped']
    ),
    contractOnGoingDefaultValue: always(
      isNotNilOrEmpty(prop('activePolicies', getSimulationPricingsDailyBenefit))
    ),
    commentDefaultValue: path(['activePolicies', 0, 'comment'])
  })(getSimulationPricingsDailyBenefit);

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      collectionNeeds: [
        {
          isDutyOfAdviceSkipped: isDutyOfAdviceSkippedDefaultValue
        }
      ],
      pricing: {
        level: pricingLevelDefaultValue
      },
      activePolicies: [
        {
          contractOnGoing: contractOnGoingDefaultValue,
          comment: commentDefaultValue
        }
      ]
    }
  });

  const levelChosen = form.watch('pricing.level');

  let { isValid } = form.formState;

  isValid = isValid || not(isNil(levelChosen));

  useEffect(() => {
    form.trigger();
  }, []);

  const isDutyOfAdviceSkipped = form.watch('collectionNeeds[0].isDutyOfAdviceSkipped');

  const contractOnGoing = form.watch('activePolicies.0.contractOnGoing');
  const comment = form.watch('activePolicies.0.comment');

  const lowerize = (prop) =>
    map(compose(fromPairs, map(over(lensIndex(0), toLower)), toPairs))(getMiddleText(prop));

  const levels = lowerize('NIVEAUXGARANTIESETTARIFS_GARANTIES_LEVELS');

  const annualPrice = findPriceProperty('annualPrice', levelChosen)(pricings) || 0;
  const monthlyPrice = findPriceProperty('monthlyPrice', levelChosen)(pricings) || 0;

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);
    const data = compose(
      when(always(!isDutyOfAdviceSkipped), assocPath(['pricing', 'level'], null)),
      when(always(!contractOnGoing), dissocPath(['activePolicies', 0])),
      when(
        always(and(contractOnGoing, isNilOrEmpty(comment))),
        assocPath(['activePolicies', 0, 'comment'], '')
      ), // When user says yes but doesn't fill any comment
      dissocPath(['activePolicies', 0, 'contractOnGoing']),
      dissoc('pricings'),
      assocPath(
        ['pricing', 'label'],
        getLabelFromValue(path(['pricing', 'level'], values), levels)
      ),
      assocPath(['pricing', 'annualPrice'], annualPrice),
      assocPath(['pricing', 'monthlyPrice'], monthlyPrice)
    )(values);
    saveSimulationPricingsDailyBenefit(data, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };
  return {
    form,
    onSubmit,
    isDutyOfAdviceSkipped,
    levels,
    annualPrice,
    monthlyPrice,
    isValid
  };
};

export default useSimulationPricingsDailyBenefitForm;
